import * as React from "react";
import styled from "styled-components";
import {
  Alert,
  Card,
  Col,
  Row,
  Select,
  Statistic,
  Table,
  Typography,
  Space,
  Tag,
  theme,
  Tooltip,
} from "antd";
import {
  AnalyticsData,
  BreakdownEntry,
  DailyCount,
  EventStat,
  GROUP_CONFIG,
  GroupMode,
  PageStat,
  formatPeriodLabel,
  formatPeriodShortLabel,
} from "./analyticsHelpers";

const { Text } = Typography;

const KpiCard = styled(Card)`
  .ant-statistic-title {
    font-size: 13px;
  }
`;

const SectionCard = styled(Card)`
  margin-bottom: 16px;
`;

const ChartBar = styled.div<{ height: number; color: string }>`
  width: 100%;
  height: ${(p) => p.height}px;
  background: ${(p) => p.color};
  border-radius: 2px 2px 0 0;
  min-height: 2px;
  transition: height 0.3s ease;
  &:hover {
    opacity: 0.85;
  }
`;

const ChartContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 220px;
  padding: 16px 0 0 0;
`;

const ChartLabel = styled.div`
  font-size: 10px;
  text-align: center;
  color: #999;
  margin-top: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChartColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
  cursor: default;
`;

const KpiRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  & > * {
    flex: 1;
    min-width: 0;
  }
  @media (max-width: 768px) {
    flex-wrap: wrap;
    & > * {
      flex: 1 1 calc(50% - 8px);
    }
  }
`;

const BreakdownRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f5f5f5;
`;

interface Props {
  data: AnalyticsData;
  loading: boolean;
  groupMode: GroupMode;
  onGroupModeChange: (m: GroupMode) => void;
}

export const AnalyticsDashboardView = React.memo(function AnalyticsDashboardView({
  data,
  loading,
  groupMode,
  onGroupModeChange,
}: Props) {
  // Farben aus dem antd-Theme statt eigener Palette, damit die Seite dem Rest
  // der Administration folgt.
  const { token } = theme.useToken();
  const maxDaily = Math.max(...data.daily.map((d) => d.count), 1);
  // Ohne echte Session-Daten (anonymer Modus) sind Session-Kennzahlen nicht
  // berechenbar → "–" statt irreführender Werte.
  const noSessionData = data.uniqueSessions === 0;
  const kpis = [
    { title: "Unique Besucher", value: data.uniqueVisitors },
    { title: "Seitenaufrufe", value: data.totalViews },
    {
      title: "Sitzungen",
      value: noSessionData ? "–" : data.uniqueSessions,
    },
    {
      title: "Bounce Rate",
      value: noSessionData ? "–" : data.bounceRate,
      suffix: noSessionData ? undefined : "%",
    },
    {
      title: "Ø Sitzungsdauer",
      value: noSessionData ? "–" : data.avgDuration,
    },
  ];

  return (
    <>
      {data.truncated && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Zeitraum zu groß — Zahlen sind unvollständig"
          description={`Es wurden nur die neuesten ${(
            data.truncatedLimit ?? 0
          ).toLocaleString("de-DE")} Aufrufe ausgewertet. Die ältesten Aufrufe des Zeitraums fehlen, alle Werte sind daher zu niedrig. Bitte einen kürzeren Zeitraum wählen.`}
        />
      )}

      <KpiRow>
        {kpis.map((kpi) => (
          <KpiCard loading={loading} key={kpi.title}>
            <Statistic
              title={kpi.title}
              value={kpi.value}
              suffix={kpi.suffix}
              valueStyle={{ fontSize: 24 }}
            />
          </KpiCard>
        ))}
      </KpiRow>

      <SectionCard
        title={`Seitenaufrufe pro ${GROUP_CONFIG[groupMode].shortLabel}`}
        loading={loading}
        extra={
          <Select
            value={groupMode}
            onChange={onGroupModeChange}
            style={{ width: 140 }}
            size="small"
            options={[
              { value: "day", label: "Pro Tag" },
              { value: "month", label: "Pro Monat" },
              { value: "year", label: "Pro Jahr" },
            ]}
          />
        }
      >
        <ChartContainer>
          {data.daily.map((d: DailyCount) => {
            const tooltipLabel = formatPeriodLabel(d.date, groupMode);
            return (
              <Tooltip key={d.date} title={`${tooltipLabel}: ${d.count} Aufrufe`}>
                <ChartColumn>
                  <ChartBar
                    height={Math.round((d.count / maxDaily) * 200)}
                    color={token.colorPrimary}
                  />
                  <ChartLabel>{formatPeriodShortLabel(d.date, groupMode)}</ChartLabel>
                </ChartColumn>
              </Tooltip>
            );
          })}
        </ChartContainer>
      </SectionCard>

      <SectionCard title="Seiten" loading={loading}>
        <Table
          dataSource={data.pages}
          rowKey="pageName"
          pagination={false}
          size="middle"
          columns={[
            {
              title: "Seite",
              dataIndex: "pageName",
              key: "pageName",
              render: (text: string) => <Text strong>{text}</Text>,
            },
            {
              title: "Aufrufe",
              dataIndex: "views",
              key: "views",
              width: 120,
              align: "right" as const,
              sorter: (a: PageStat, b: PageStat) => a.views - b.views,
              defaultSortOrder: "descend" as const,
            },
            {
              title: "Ø Verweildauer",
              dataIndex: "avgDurationLabel",
              key: "avgDurationLabel",
              width: 150,
              align: "right" as const,
              sorter: (a: PageStat, b: PageStat) =>
                a.avgDurationSec - b.avgDurationSec,
            },
          ]}
        />
      </SectionCard>

      {data.events.length > 0 && (
        <SectionCard title="Events" loading={loading}>
          <Table
            dataSource={data.events}
            rowKey={(r) => `${r.eventType}:${r.label}`}
            pagination={false}
            size="middle"
            columns={[
              {
                title: "Typ",
                dataIndex: "eventType",
                key: "eventType",
                width: 150,
                render: (text: string) => <Tag color="purple">{text}</Tag>,
              },
              { title: "Name", dataIndex: "label", key: "label" },
              {
                title: "Anzahl",
                dataIndex: "count",
                key: "count",
                width: 120,
                align: "right" as const,
                sorter: (a: EventStat, b: EventStat) => a.count - b.count,
                defaultSortOrder: "descend" as const,
              },
            ]}
          />
        </SectionCard>
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <BreakdownCol title="Geräte" entries={data.devices} loading={loading} />
        <BreakdownCol title="Browser" entries={data.browsers} loading={loading} />
        <BreakdownCol
          title="Sprachen (alle Aufrufe)"
          entries={data.languages}
          loading={loading}
        />
        <BreakdownCol
          title="Initiale Sprache pro Sitzung"
          entries={data.initialLanguages}
          loading={loading}
        />
        <BreakdownCol
          title="Sprache pro Unique Besucher"
          entries={data.visitorLanguages}
          loading={loading}
        />
      </Row>
    </>
  );
});

function BreakdownCol({
  title,
  entries,
  loading,
}: {
  title: string;
  entries: BreakdownEntry[];
  loading: boolean;
}) {
  return (
    <Col xs={24} md={12}>
      <SectionCard title={title} loading={loading}>
        {entries.map((e) => (
          <BreakdownRow key={e.label}>
            <Text>{e.label}</Text>
            <Space>
              <Text type="secondary">{e.count}</Text>
              <Tag>{e.percent}%</Tag>
            </Space>
          </BreakdownRow>
        ))}
      </SectionCard>
    </Col>
  );
}
